/**
 * PENDING WORK — the ONE registry of "what is waiting for a human, per lot".
 *
 * Why this exists (Doug, 2026-08-20): he opened /operations/pay-change-requests
 * at random, found requests waiting, and approved them. Measured after the fact:
 * six requests approved that night, average wait 30.0 days, worst 42.4 days.
 * Those are valet pay corrections. Valets waited six weeks for money because no
 * surface anywhere said the queue existed. The screen worked perfectly; nothing
 * ever announced it.
 *
 * ONE definition per concept. A lot declares what "pending" means for it HERE —
 * never inline in a screen — so the sidebar, and any future surface that wants
 * the same number, cannot drift from each other.
 *
 * Contract for an entry:
 *   lotId    key must match the lot `id` in src/constants/garages.js, which is
 *            what the sidebar renders against.
 *   table    a table the browser role can actually SELECT (RLS applies).
 *   filters  applied with .eq()/.in(); keep it to what "needs a human" means.
 *   rpc      instead of table+filters: a zero-arg RPC returning the count, for a definition a table filter
 *            cannot express. It must return 0 for callers who may not see the work.
 *   plural   optional, for nouns that do not pluralise with an s.
 *   noun     the countable thing, SINGULAR — pluralised on the noun, not the phrase.
 *   waiting  what it is waiting for; completes the tooltip sentence.
 *
 * Keep this list SHORT and true. A counter that cries wolf gets ignored, which is
 * the 256-stale-cards lesson; a badge on a lot where nothing is really actionable
 * costs more attention than it saves.
 */

export const PENDING_WORK = {
  'pay-change-requests': {
    table: 'pay_change_requests',
    filters: { status: 'pending' },
    noun: 'pay change request',
    waiting: 'awaiting review',
  },
  // Marketing (lot id 'crm'): prospect replies nobody has answered yet. Not a table count: "waiting" means a
  // reply event with no later reply_handled event and the lead not won/lost, defined once in
  // lead_engine_api._reply_rows. The RPC returns 0 for anyone who is not council.
  crm: {
    rpc: 'lead_engine_replies_waiting',
    noun: 'prospect reply',
    plural: 'prospect replies',
    waiting: 'waiting for an answer',
  },
};

/** Lot ids that declare a pending-work count. */
export const PENDING_WORK_LOTS = Object.keys(PENDING_WORK);

/** "3 pay change requests awaiting review" — the tooltip sentence. */
export function pendingLabel(lotId, count) {
  const entry = PENDING_WORK[lotId];
  if (!entry || !count) return '';
  // Pluralise the NOUN, not the end of the phrase: "29 pay change requests
  // awaiting review", never "29 pay change request awaiting reviews".
  const noun = count === 1 ? entry.noun : (entry.plural || `${entry.noun}s`);
  return `${count} ${noun} ${entry.waiting}`;
}
